import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    Truck,
    Wrench,
    Hammer,
    Monitor,
    Smartphone,
    Anchor,
    Armchair,
    Package,
    Calendar as CalendarIcon,
    DollarSign,
    MapPin,
    User,
    Building2,
    Settings,
    Plus,
    Edit,
    Archive,
    History,
    Activity,
    ShieldCheck,
    AlertCircle,
    Bell,
    CheckCircle2,
    FileText,
    MoreHorizontal,
    ArrowRight,
    Eye,
    LayoutGrid,
    ClipboardCheck,
    Download,
    Play,
    XCircle,
    Info,
    Clock,
    AlertTriangle,
    Search,
    Trash2,
    Box,
    ArrowLeft,
    ChevronDown,
    Printer,
    Zap,
    QrCode
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toSydneyYYYYMMDD, toSydneyDate, formatSydneyTime } from '../../utils/helpers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "../../components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { cn } from '../../components/ui/utils';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Asset, AssetAssignment, MaintenanceRecord, RepairRecord, AssetStatus, AssetCondition, AssetStatusHistory, AssetReminder, AssetNote } from '../../types/asset';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';
import AssetDocuments from './components/AssetDocuments';
import InspectionQRModal from './components/InspectionQRModal';

const AssetDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const { currentUser } = useAuth();

    if (!ability.can(ACTIONS.READ, RESOURCES.ASSET)) {
        return <AccessRestricted />;
    }

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showUnassignModal, setShowUnassignModal] = useState(false);
    const [showFinalStatusModal, setShowFinalStatusModal] = useState(false);
    const [showArchiveAlert, setShowArchiveAlert] = useState<{ isOpen: boolean, canArchive: boolean, message: string }>({ isOpen: false, canArchive: false, message: '' });
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showDeleteReminderAlert, setShowDeleteReminderAlert] = useState(false);
    const [reminderToDelete, setReminderToDelete] = useState<number | null>(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isQRModalOpen, setIsQRModalOpen] = useState(false);
    const [showDeleteMaintenanceAlert, setShowDeleteMaintenanceAlert] = useState(false);
    const [maintenanceToDelete, setMaintenanceToDelete] = useState<number | null>(null);
    const [showDeleteRepairAlert, setShowDeleteRepairAlert] = useState(false);
    const [repairToDelete, setRepairToDelete] = useState<number | null>(null);
    const [showDeleteDraftAlert, setShowDeleteDraftAlert] = useState(false);
    const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
    const [pendingStatus, setPendingStatus] = useState<AssetStatus | null>(null);
    const [asset, setAsset] = useState<Asset | null>(null);
    const [history, setHistory] = useState<{
        assignments: AssetAssignment[];
        maintenance: MaintenanceRecord[];
        repairs: RepairRecord[];
        statusChanges: AssetStatusHistory[];
        inspections: any[];
        reminders: AssetReminder[];
        notes: AssetNote[];
    }>({
        assignments: [],
        maintenance: [],
        repairs: [],
        statusChanges: [],
        inspections: [],
        reminders: [],
        notes: []
    });
    const [activeTab, setActiveTab] = useState('assignments');
    const [remindersLoading, setRemindersLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [hasInspectionTemplate, setHasInspectionTemplate] = useState(false);

    const [completeMaintenanceModalOpen, setCompleteMaintenanceModalOpen] = useState(false);
    const [selectedMaintenanceRecord, setSelectedMaintenanceRecord] = useState<MaintenanceRecord | null>(null);
    const [maintenanceCompletionData, setMaintenanceCompletionData] = useState({
        completedDate: new Date().toISOString().split('T')[0],
        cost: '',
        description: '',
        setupReminder: false,
        type: '',
        vendor: '',
        performedBy: '',
        scheduledDate: '',
        scheduledTime: ''
    });
    const [maintenanceModalMode, setMaintenanceModalMode] = useState<'complete' | 'edit'>('edit');
    const [completingMaintenance, setCompletingMaintenance] = useState(false);
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNoteText, setNewNoteText] = useState('');

    // Delete state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [selectedApprover, setSelectedApprover] = useState('');
    const [approvers, setApprovers] = useState<{ id: number; name: string; userRole: string; email: string }[]>([]);
    const [deleting, setDeleting] = useState(false);

    const tabOptions = [
        { id: 'assignments', label: 'Assignment History', count: history.assignments.length, action: ACTIONS.READ, resource: RESOURCES.ASSET_ASSIGNMENT },
        { id: 'maintenance', label: 'Maintenance', count: history.maintenance.length, action: ACTIONS.READ, resource: RESOURCES.ASSET_MAINTENANCE_HISTORY },
        { id: 'repairs', label: 'Repairs', count: history.repairs.length, action: ACTIONS.READ, resource: RESOURCES.ASSET_REPAIR },
        { id: 'reminders', label: 'Reminders', count: history.reminders.length, action: ACTIONS.READ, resource: RESOURCES.ASSET_REMINDER },
        { id: 'inspections', label: 'Inspections', count: history.inspections.length, action: ACTIONS.READ, resource: RESOURCES.ASSET_INSPECTION },
        { id: 'documents', label: 'Documents', count: 0, action: ACTIONS.READ, resource: RESOURCES.ASSET },
        { id: 'history', label: 'Status History', count: history.statusChanges.length, action: ACTIONS.READ, resource: RESOURCES.ASSET },
        { id: 'notes', label: 'Notes', count: history.notes.length, action: ACTIONS.READ, resource: RESOURCES.ASSET },
    ].filter(tab => ability.can(tab.action, tab.resource));
    const activeTabOption = tabOptions.find(t => t.id === activeTab);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch core asset data first as we need it for everything else
            const assetData = await api.get<Asset>(`/api/assets/${id}`);
            setAsset(assetData);

            // Fetch other data pieces only if user has permission
            const [historyData, inspectionsData, remindersData, notesData] = await Promise.all([
                ability.can(ACTIONS.READ, RESOURCES.ASSET_MAINTENANCE_HISTORY)
                    ? api.get<any>(`/api/assets/${id}/history`)
                    : Promise.resolve({ assignments: [], maintenance: [], repairs: [], statusChanges: [] }),
                ability.can(ACTIONS.READ, RESOURCES.ASSET_INSPECTION)
                    ? api.get<any[]>(`/api/assets/${id}/inspections`)
                    : Promise.resolve([]),
                ability.can(ACTIONS.READ, RESOURCES.ASSET_REMINDER)
                    ? api.get<AssetReminder[]>(`/api/assets/${id}/reminders`)
                    : Promise.resolve([]),
                // Notes uses RESOURCES.ASSET check
                ability.can(ACTIONS.READ, RESOURCES.ASSET)
                    ? api.get<AssetNote[]>(`/api/assets/${id}/notes`)
                    : Promise.resolve([])
            ]);

            setHistory({
                ...historyData,
                inspections: inspectionsData || [],
                reminders: remindersData || [],
                notes: notesData || []
            });

            // Check for inspection templates - only if user can conduct inspections
            if (ability.can(ACTIONS.CREATE, RESOURCES.ASSET_INSPECTION)) {
                const templatesRes = await api.get<any[]>(`/api/inspection-templates?assetType=${encodeURIComponent(assetData.type)}&status=Active`);
                setHasInspectionTemplate(templatesRes && templatesRes.length > 0);
            }
        } catch (err) {
            console.error('Error fetching asset details:', err);
            toast.error('Failed to load asset details');
        } finally {
            setLoading(false);
        }
    }, [id, ability]);

    const handleDeleteReminder = (reminderId: number) => {
        setReminderToDelete(reminderId);
        setShowDeleteReminderAlert(true);
    };

    const confirmDeleteReminder = async () => {
        if (reminderToDelete === null) return;
        try {
            await api.delete(`/api/assets/${id}/reminders/${reminderToDelete}`);
            toast.success('Reminder deleted successfully');
            fetchData();
        } catch (error: any) {
            console.error('Error deleting reminder:', error);
            toast.error(error.response?.data?.message || 'Failed to delete reminder');
        } finally {
            setReminderToDelete(null);
            setShowDeleteReminderAlert(false);
        }
    };

    const handleDeleteMaintenance = (maintenanceId: number) => {
        setMaintenanceToDelete(maintenanceId);
        setShowDeleteMaintenanceAlert(true);
    };

    const confirmDeleteMaintenance = async () => {
        if (maintenanceToDelete === null) return;
        try {
            await api.delete(`/api/assets/${id}/maintenance/${maintenanceToDelete}`);
            toast.success('Maintenance record deleted successfully');
            fetchData();
        } catch (error: any) {
            console.error('Error deleting maintenance record:', error);
            toast.error(error.response?.data?.message || 'Failed to delete maintenance record');
        } finally {
            setMaintenanceToDelete(null);
            setShowDeleteMaintenanceAlert(false);
        }
    };

    const handleDeleteRepair = (repairId: number) => {
        setRepairToDelete(repairId);
        setShowDeleteRepairAlert(true);
    };

    const confirmDeleteRepair = async () => {
        if (repairToDelete === null) return;
        try {
            await api.delete(`/api/assets/${id}/repair/${repairToDelete}`);
            toast.success('Repair record deleted successfully');
            fetchData();
        } catch (error: any) {
            console.error('Error deleting repair record:', error);
            toast.error(error.response?.data?.message || 'Failed to delete repair record');
        } finally {
            setRepairToDelete(null);
            setShowDeleteRepairAlert(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNoteText.trim()) return;

        try {
            setNotesLoading(true);
            const response = await api.post<AssetNote>(`/api/assets/${id}/notes`, { text: newNoteText });
            setHistory(prev => ({
                ...prev,
                notes: [response, ...prev.notes]
            }));
            setNewNoteText('');
            toast.success('Note added successfully');
        } catch (error: any) {
            console.error('Error adding note:', error);
            toast.error(error.response?.data?.message || 'Failed to add note');
        } finally {
            setNotesLoading(false);
        }
    };

    const handleAddAsset = () => {
        navigate('/assets/add');
    };

    const handleDeleteDraft = (draftId: string) => {
        setDraftToDelete(draftId);
        setShowDeleteDraftAlert(true);
    };

    const confirmDeleteDraft = async () => {
        if (draftToDelete === null) return;
        try {
            await api.delete(`/api/assets/${id}/inspections/${draftToDelete}`);
            toast.success('Draft deleted successfully');
            fetchData();
        } catch (error: any) {
            console.error('Error deleting draft:', error);
            toast.error(error.response?.data?.message || 'Failed to delete draft');
        } finally {
            setDraftToDelete(null);
            setShowDeleteDraftAlert(false);
        }
    };

    const resetMaintenanceModal = (record: any, mode: 'edit' | 'complete') => {
        setSelectedMaintenanceRecord(record);
        setMaintenanceModalMode(mode);
        setMaintenanceCompletionData({
            completedDate: mode === 'complete'
                ? toSydneyYYYYMMDD(new Date())
                : (record.completedDate ? toSydneyYYYYMMDD(record.completedDate) : ''),
            cost: record.cost ? record.cost.toString() : '',
            description: record.description || '',
            setupReminder: false,
            type: record.type || '',
            vendor: record.vendor || '',
            performedBy: record.performedBy || '',
            scheduledDate: record.scheduledDate ? toSydneyYYYYMMDD(record.scheduledDate) : '',
            scheduledTime: record.scheduledTime || ''
        });
        setCompleteMaintenanceModalOpen(true);
    };

    const handleArchiveClick = () => {
        if (!asset) return;
        let canArchive = false;
        let message = '';

        switch (asset.status) {
            case 'Assigned':
                canArchive = false;
                message = 'This asset cannot be archived while it is Assigned. Please return it first.';
                break;
            case 'In Maintenance':
                canArchive = false;
                message = 'This asset can not be archived while it is in maintenance. Please complete or close maintenance first.';
                break;
            case 'Available':
            case 'Needs Repair':
            case 'Lost':
                canArchive = true;
                message = 'Are you sure you want to archive this asset?';
                break;
            default:
                canArchive = true;
                message = 'Are you sure you want to archive this asset?';
        }

        setShowArchiveAlert({ isOpen: true, canArchive, message });
    };

    // Delete handlers
    const handleDeleteClick = () => {
        if (!asset) return;
        const deletableStatuses = ['Damaged', 'Needs Repair', 'Disposed', 'Lost'];
        if (deletableStatuses.includes(asset.status)) {
            setSelectedApprover('');
            setShowDeleteConfirm(true);
        } else {
            setShowDeleteWarning(true);
        }
    };

    const confirmDelete = async () => {
        if (!asset || !selectedApprover) return;
        try {
            setDeleting(true);
            await api.post(`/api/assets/${asset.id}/soft-delete`, { approvedById: parseInt(selectedApprover) });
            toast.success(`${asset.name} has been moved to the recycle bin.`);
            navigate('/assets');
        } catch (error: any) {
            console.error('Error deleting asset:', error);
            toast.error(error.response?.data?.message || 'Failed to delete asset');
        } finally {
            setDeleting(false);
        }
    };

    // Fetch approvers
    useEffect(() => {
        const fetchApprovers = async () => {
            try {
                const data = await api.get<{ id: number; name: string; userRole: string; email: string }[]>('/api/assets/approvers');
                setApprovers(data);
            } catch (error) {
                console.error('Error fetching approvers:', error);
            }
        };
        fetchApprovers();
    }, []);

    const handleCompleteMaintenance = async () => {
        if (!selectedMaintenanceRecord || !asset) return;
        try {
            setCompletingMaintenance(true);
            const isUpdate = maintenanceModalMode === 'edit';
            const endpoint = isUpdate
                ? `/api/assets/${asset.id}/maintenance/${selectedMaintenanceRecord.id}`
                : `/api/assets/${asset.id}/maintenance/${selectedMaintenanceRecord.id}/complete`;

            // Use patch/patch for consistency
            await api.patch(endpoint, maintenanceCompletionData);

            fetchData();

            toast.success(isUpdate ? 'Maintenance record updated' : 'Maintenance marked as complete');
            setCompleteMaintenanceModalOpen(false);

            if (!isUpdate && maintenanceCompletionData.setupReminder) {
                navigate(`/assets/${asset.id}/reminders/new?prefill=true&name=${encodeURIComponent('Reminder for next estimated inspection date')}&type=Schedule Maintenance`);
            }

            setSelectedMaintenanceRecord(null);
            setMaintenanceCompletionData({
                completedDate: new Date().toISOString().split('T')[0],
                cost: '',
                description: '',
                setupReminder: false,
                type: '',
                vendor: '',
                performedBy: '',
                scheduledDate: '',
                scheduledTime: ''
            });
        } catch (err: any) {
            console.error('Error completing maintenance:', err);
            toast.error(err.response?.data?.message || 'Failed to complete maintenance');
        } finally {
            setCompletingMaintenance(false);
        }
    };

    useEffect(() => {
        if (id) fetchData();
    }, [id, fetchData]);

    const getStatusStyles = (status: AssetStatus) => {
        switch (status) {
            case 'Assigned':
                return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'Available':
                return 'bg-green-50 text-green-600 border-green-100';
            case 'In Maintenance':
                return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'Needs Repair':
            case 'Damaged':
                return 'bg-red-50 text-red-600 border-red-100';
            default:
                return 'text-gray-600 bg-gray-100/50';
        }
    };



    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'Vehicle': return { icon: <Truck size={20} />, bg: 'bg-blue-50', text: 'text-blue-600' };
            case 'Plant & Equipment': return { icon: <Wrench size={20} />, bg: 'bg-emerald-50', text: 'text-emerald-600' };
            case 'Tools': return { icon: <Hammer size={20} />, bg: 'bg-orange-50', text: 'text-orange-600' };
            case 'IT Equipment': return { icon: <Monitor size={20} />, bg: 'bg-indigo-50', text: 'text-indigo-600' };
            case 'Mobile Devices': return { icon: <Smartphone size={20} />, bg: 'bg-pink-50', text: 'text-pink-600' };
            case 'Lifting/Slings': return { icon: <Anchor size={20} />, bg: 'bg-amber-50', text: 'text-amber-600' };
            case 'Furniture': return { icon: <Armchair size={20} />, bg: 'bg-teal-50', text: 'text-teal-600' };
            default: return { icon: <Package size={20} />, bg: 'bg-gray-50', text: 'text-gray-600' };
        }
    };

    const getValidTransitions = (currentStatus: AssetStatus) => {
        let allowedPaths: AssetStatus[] = [];

        switch (currentStatus) {
            case 'In Triage':
                allowedPaths = ['Available', 'In Maintenance', 'Needs Repair', 'Damaged', 'Assigned'];
                break;
            case 'Assigned':
                allowedPaths = ['Available', 'In Maintenance', 'Needs Repair', 'Lost'];
                break;
            case 'In Maintenance':
                allowedPaths = ['Assigned', 'Available', 'Needs Repair', 'Damaged'];
                break;
            case 'Needs Repair':
                const isAssigned = asset?.assignedToUser || asset?.assignedToDepartment;
                allowedPaths = ['Damaged', 'In Maintenance', 'Retired'];
                if (isAssigned) {
                    allowedPaths.push('In Triage');
                } else {
                    allowedPaths.push('Available');
                }
                break;
            case 'Available':
                allowedPaths = ['In Triage', 'In Maintenance', 'Needs Repair', 'Damaged', 'Assigned', 'Lost'];
                break;
            case 'Lost':
                allowedPaths = ['Disposed'];
                break;
            case 'Damaged':
                allowedPaths = ['Needs Repair', 'Retired', 'Disposed'];
                break;
            case 'Retired':
            case 'Disposed':
                allowedPaths = [];
                break;
            default:
                allowedPaths = ['In Triage', 'Available', 'Assigned', 'In Maintenance', 'Needs Repair', 'Damaged', 'Archived', 'Disposed', 'Lost', 'Retired'];
        }

        const filteredWorkflow: Record<string, any[]> = {
            'Workflow': [],
            'Maintenance': [],
            'Issues': [],
            'Final': []
        };

        const allItems = [
            { id: 'In Triage', icon: <Clock size={16} />, section: 'Workflow', label: 'In Triage', description: 'Newly added or returned asset awaiting review' },
            { id: 'Available', icon: <CheckCircle2 size={16} />, section: 'Workflow', label: 'Available', description: 'Ready for use' },
            { id: 'Assigned', icon: <Play size={16} />, section: 'Workflow', label: 'Assigned', description: 'Currently assigned and in use' },
            { id: 'In Maintenance', icon: <Settings size={16} />, section: 'Maintenance', label: 'In Maintenance', description: 'Undergoing maintenance' },
            { id: 'Needs Repair', icon: <AlertTriangle size={16} />, section: 'Issues', label: 'Needs Repair', description: 'Faulty; awaiting repair' },
            { id: 'Damaged', icon: <XCircle size={16} />, section: 'Issues', label: 'Damaged', description: 'Physically unusable' },
            { id: 'Lost', icon: <Search size={16} />, section: 'Issues', label: 'Lost', description: 'Missing and unaccounted for' },
            { id: 'Retired', icon: <Archive size={16} />, section: 'Final', label: 'Retired', description: 'Formally removed from active inventory' },
            { id: 'Disposed', icon: <Trash2 size={16} />, section: 'Final', label: 'Disposed', description: 'Sold, scrapped, or permanently removed' }
        ];

        allItems.forEach(item => {
            if (allowedPaths.includes(item.id as AssetStatus) && item.id !== currentStatus) {
                if (filteredWorkflow[item.section]) {
                    filteredWorkflow[item.section].push(item);
                }
            }
        });

        // Remove empty sections
        Object.keys(filteredWorkflow).forEach(key => {
            if (filteredWorkflow[key].length === 0) {
                delete filteredWorkflow[key];
            }
        });

        return filteredWorkflow;
    };

    const handleStatusUpdate = async (newStatus: AssetStatus) => {
        if (!asset || asset.status === newStatus) return;

        if (['Available', 'In Triage'].includes(asset.status) && newStatus === 'Assigned') {
            setShowAssignModal(true);
            return;
        }

        if (['Retired', 'Disposed'].includes(newStatus)) {
            setPendingStatus(newStatus);
            setShowFinalStatusModal(true);
            return;
        }

        const unassigningStatuses = ['Available', 'Lost', 'Archived'];
        const isCurrentlyAssigned = asset.status === 'Assigned' || asset.assignedToUser || asset.assignedToDepartment;

        if (isCurrentlyAssigned && (newStatus === 'Available')) {
            navigate(`/assets/${asset.id}/return`, { state: { targetStatus: newStatus } });
            return;
        }

        if (isCurrentlyAssigned && unassigningStatuses.includes(newStatus)) {
            setPendingStatus(newStatus);
            setShowUnassignModal(true);
            return;
        }

        await processStatusUpdate(newStatus);
    };

    const processStatusUpdate = async (newStatus: AssetStatus) => {
        try {
            await api.post(`/api/assets/${id}/status-workflow`, { status: newStatus });
            setAsset(prev => prev ? { ...prev, status: newStatus } : null);

            // Refresh history
            fetchData();

            toast.success(`Status updated to ${newStatus}`);
        } catch (err) {
            console.error('Error updating status:', err);
            toast.error('Failed to update status');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!asset) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">Asset not found</h2>
                <Button onClick={() => navigate('/assets')}>Back to Dashboard</Button>
            </div>
        );
    }

    const typeInfo = getTypeIcon(asset.type);

    return (
        <div className="max-w-[1400px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                        onClick={() => navigate('/assets')}
                    >
                        <ChevronLeft size={20} />
                    </Button>
                    <div className={cn("p-3 rounded-2xl shrink-0", typeInfo.bg, typeInfo.text)}>
                        {typeInfo.icon}
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            {asset.name} - {asset.tag}
                        </h1>
                        <div className="flex items-center gap-2 text-gray-500 text-sm font-medium mt-0.5">
                            <span>{asset.tag}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                            <span>{asset.type}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-2">
                        {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT) && (
                            (asset.status === 'Assigned' || asset.assignedToUser || asset.assignedToDepartment) ? (
                                <>
                                    <Button
                                        className="rounded-xl h-10 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs gap-2 flex items-center shadow-sm"
                                        onClick={() => navigate(`/assets/${asset.id}/reassign`)}
                                    >
                                        <ArrowRight size={14} />
                                        Reassign Asset
                                    </Button>
                                    <Button
                                        className="rounded-xl h-10 bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs gap-2 flex items-center shadow-sm"
                                        onClick={() => handleStatusUpdate('In Triage')}
                                    >
                                        <Clock size={14} />
                                        Send to Triage
                                    </Button>
                                    {asset.status !== 'Needs Repair' && (
                                        <Button
                                            className="rounded-xl h-10 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs gap-2 flex items-center shadow-sm"
                                            onClick={() => navigate(`/assets/${asset.id}/return`, { state: { targetStatus: 'Available' } })}
                                        >
                                            <ArrowLeft size={14} />
                                            Return Asset
                                        </Button>
                                    )}
                                </>
                            ) : (asset.status === 'Available') ? (
                                <Button
                                    className="rounded-xl h-10 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs gap-2 flex items-center shadow-sm"
                                    onClick={() => navigate('/assets/assign', { state: { preSelectedAsset: asset.id.toString() } })}
                                >
                                    <User size={14} />
                                    Assign Asset
                                </Button>
                            ) : null
                        )}

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="rounded-xl h-10 border-gray-100 font-bold text-xs gap-2 items-center bg-white"
                                >
                                    <MoreHorizontal size={14} />
                                    More Actions
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[200px] p-2 rounded-2xl border-gray-100 shadow-xl">
                                {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET) && (
                                    <DropdownMenuItem
                                        className="rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        onClick={() => navigate(`/assets/${asset.id}/edit`)}
                                    >
                                        <Edit size={14} className="text-gray-400" />
                                        Edit Details
                                    </DropdownMenuItem>
                                )}

                                <DropdownMenuSeparator className="bg-gray-50 mx-2" />

                                {ability.can(ACTIONS.CREATE, RESOURCES.ASSET_MAINTENANCE) && (
                                    <DropdownMenuItem
                                        className="rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        onClick={() => navigate(`/assets/${asset.id}/maintenance/new`)}
                                    >
                                        <Settings size={14} className="text-gray-400" />
                                        Schedule Maintenance
                                    </DropdownMenuItem>
                                )}

                                {ability.can(ACTIONS.CREATE, RESOURCES.ASSET_REPAIR) && (
                                    <DropdownMenuItem
                                        className="rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        onClick={() => navigate(`/assets/${asset.id}/repair/new`)}
                                    >
                                        <Wrench size={14} className="text-gray-400" />
                                        Record Repair
                                    </DropdownMenuItem>
                                )}

                                <DropdownMenuSeparator className="bg-gray-50 mx-2" />

                                <DropdownMenuItem
                                    className="rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                    onClick={() => navigate('/assets/print', { state: { assets: [asset] } })}
                                >
                                    <Printer size={14} className="text-gray-400" />
                                    Print Label
                                </DropdownMenuItem>

                                <DropdownMenuSeparator className="bg-gray-50 mx-2" />

                                {ability.can(ACTIONS.ARCHIVE, RESOURCES.ASSET) && asset.status !== 'Archived' && (
                                    <DropdownMenuItem
                                        className="rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        onClick={handleArchiveClick}
                                    >
                                        <Archive size={14} className="text-gray-400" />
                                        Archive Asset
                                    </DropdownMenuItem>
                                )}

                                {ability.can(ACTIONS.ARCHIVE, RESOURCES.ASSET) && asset.status === 'Archived' && (
                                    <DropdownMenuItem
                                        className="rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all hover:bg-blue-50 flex items-center gap-3 text-blue-600"
                                        onClick={() => setShowRestoreModal(true)}
                                    >
                                        <History size={14} className="text-blue-500" />
                                        Restore Asset
                                    </DropdownMenuItem>
                                )}

                                {ability.can(ACTIONS.DELETE, RESOURCES.ASSET) && (
                                    <DropdownMenuItem
                                        className="rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer transition-all hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        onClick={handleDeleteClick}
                                    >
                                        <Trash2 size={14} />
                                        Delete Asset
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    {/* Mobile Actions Dropdown */}
                    <div className="md:hidden w-full">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="flex items-center gap-2 w-full justify-center rounded-xl bg-white border-gray-200 text-gray-700 font-bold h-11"
                                >
                                    <MoreHorizontal size={18} />
                                    <span>Actions</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64 p-2 rounded-2xl border-gray-100 shadow-xl">
                                <DropdownMenuItem onClick={() => navigate('/assets/print', { state: { assets: [asset] } })} className="rounded-xl py-2.5 font-bold text-gray-700 focus:bg-gray-50 flex items-center gap-2 cursor-pointer">
                                    <Printer size={16} className="text-gray-400" />
                                    Print Label
                                </DropdownMenuItem>
                                {ability.can(ACTIONS.CREATE, RESOURCES.ASSET_MAINTENANCE) && (
                                    <DropdownMenuItem onClick={() => navigate(`/assets/${asset.id}/maintenance/new`)} className="rounded-xl py-2.5 font-bold text-gray-700 focus:bg-gray-50 flex items-center gap-2 cursor-pointer">
                                        <Settings size={16} className="text-gray-400" />
                                        Schedule Maintenance
                                    </DropdownMenuItem>
                                )}
                                {ability.can(ACTIONS.CREATE, RESOURCES.ASSET_REPAIR) && (
                                    <DropdownMenuItem onClick={() => navigate(`/assets/${asset.id}/repair/new`)} className="rounded-xl py-2.5 font-bold text-gray-700 focus:bg-gray-50 flex items-center gap-2 cursor-pointer">
                                        <Wrench size={16} className="text-gray-400" />
                                        Record Repair
                                    </DropdownMenuItem>
                                )}

                                <DropdownMenuSeparator className="bg-gray-50 my-1" />

                                {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT) && (
                                    (asset.status === 'Assigned' || asset.assignedToUser || asset.assignedToDepartment) ? (
                                        <>
                                            <DropdownMenuItem onClick={() => navigate(`/assets/${asset.id}/reassign`)} className="rounded-xl py-2.5 font-bold text-indigo-600 focus:bg-indigo-50 flex items-center gap-2 cursor-pointer">
                                                <ArrowRight size={16} />
                                                Reassign Asset
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate(`/assets/${asset.id}/return`, { state: { targetStatus: 'In Triage' } })} className="rounded-xl py-2.5 font-bold text-orange-600 focus:bg-orange-50 flex items-center gap-2 cursor-pointer">
                                                <ArrowLeft size={16} />
                                                Return Asset
                                            </DropdownMenuItem>
                                        </>
                                    ) : (asset.status === 'Available') ? (
                                        <DropdownMenuItem onClick={() => navigate('/assets/assign', { state: { preSelectedAsset: asset.id.toString() } })} className="rounded-xl py-2.5 font-bold text-blue-600 focus:bg-blue-50 flex items-center gap-2 cursor-pointer">
                                            <User size={16} />
                                            Assign Asset
                                        </DropdownMenuItem>
                                    ) : null
                                )}

                                {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET) && (
                                    <DropdownMenuItem onClick={() => navigate(`/assets/${asset.id}/edit`)} className="rounded-xl py-2.5 font-bold text-gray-700 focus:bg-gray-50 flex items-center gap-2 cursor-pointer">
                                        <Edit size={16} className="text-gray-400" />
                                        Edit Details
                                    </DropdownMenuItem>
                                )}

                                {ability.can(ACTIONS.ARCHIVE, RESOURCES.ASSET) && asset.status !== 'Archived' && (
                                    <DropdownMenuItem onClick={handleArchiveClick} className="rounded-xl py-2.5 font-bold text-red-600 focus:bg-red-50 flex items-center gap-2 cursor-pointer">
                                        <Archive size={16} />
                                        Archive Asset
                                    </DropdownMenuItem>
                                )}
                                {ability.can(ACTIONS.ARCHIVE, RESOURCES.ASSET) && asset.status === 'Archived' && (
                                    <DropdownMenuItem onClick={() => setShowRestoreModal(true)} className="rounded-xl py-2.5 font-bold text-blue-600 focus:bg-blue-50 flex items-center gap-2 cursor-pointer">
                                        <History size={16} />
                                        Restore Asset
                                    </DropdownMenuItem>
                                )}
                                {ability.can(ACTIONS.DELETE, RESOURCES.ASSET) && (
                                    <DropdownMenuItem onClick={handleDeleteClick} className="rounded-xl py-2.5 font-bold text-red-600 focus:bg-red-50 flex items-center gap-2 cursor-pointer">
                                        <Trash2 size={16} />
                                        Delete Asset
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content Column */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Asset Overview Card */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm">
                        <h3 className="font-bold text-gray-900 text-base mb-6">Asset Overview</h3>
                        <div className="flex flex-col gap-8">
                            <div className="grid grid-cols-2 md:grid-cols-2 gap-y-6 gap-x-12">
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Brand & Model</span>
                                    <p className="text-sm font-bold text-gray-900">{asset.brand} {asset.model}</p>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Serial Number</span>
                                    <p className="text-sm font-bold text-gray-900">{asset.serialNumber || 'Not provided'}</p>
                                </div>
                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Status</span>
                                    <div className="flex items-center gap-2">
                                        <Badge className={cn("px-2.5 py-0.5 rounded-lg border-0 text-[10px] font-bold flex items-center gap-1.5", getStatusStyles(asset.status))}>
                                            <div className={cn("w-1.5 h-1.5 rounded-full",
                                                asset.status === 'Assigned' ? 'bg-blue-600' :
                                                    asset.status === 'Available' ? 'bg-green-600' :
                                                        asset.status === 'Needs Repair' ? 'bg-red-600' : 'bg-amber-600'
                                            )} />
                                            {asset.status}
                                        </Badge>
                                        <Badge variant="outline" className="px-2.5 py-0.5 rounded-lg border-gray-100 text-blue-600 text-[10px] font-bold">
                                            Workflow
                                        </Badge>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Condition</span>
                                    <p className="text-sm font-bold text-blue-600">{asset.condition}</p>
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Description</span>
                                    <p className="text-sm font-medium text-gray-500 leading-relaxed">
                                        {asset.description || 'No description provided for this asset.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Electrical Register Section */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <div className={cn("p-2 rounded-xl", asset.addToElectricalRegister ? "bg-amber-50 text-amber-600" : "bg-gray-50 text-gray-400")}>
                                <Zap size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-base">Electrical Register</h3>
                                <p className="text-gray-500 text-xs font-medium">Electrical safety testing tracking</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Register Status</span>
                                {asset.addToElectricalRegister ? (
                                    <Badge className="px-2.5 py-0.5 rounded-lg border-0 text-[10px] font-bold bg-amber-50 text-amber-600 border-amber-100">
                                        Added to Register
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="px-2.5 py-0.5 rounded-lg text-[10px] font-bold text-gray-500 border-gray-200">
                                        Not Required
                                    </Badge>
                                )}
                            </div>
                            {asset.addToElectricalRegister && (
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Testing Cadence</span>
                                    {asset.electricalCadenceNumber ? (
                                        <p className="text-sm font-bold text-gray-900">
                                            Every {asset.electricalCadenceNumber} {asset.electricalCadenceUnit}
                                        </p>
                                    ) : (
                                        <p className="text-sm font-medium text-gray-500 italic">
                                            Not specified
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Lifting Equipment Register Section */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <div className={cn("p-2 rounded-xl", asset.addToLiftingRegister ? "bg-orange-50 text-orange-600" : "bg-gray-50 text-gray-400")}>
                                <Anchor size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-base">Lifting Equipment Register</h3>
                                <p className="text-gray-500 text-xs font-medium">Lifting gear and sling compliance tracking</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Register Status</span>
                                {asset.addToLiftingRegister ? (
                                    <Badge className="px-2.5 py-0.5 rounded-lg border-0 text-[10px] font-bold bg-orange-50 text-orange-600 border-orange-100">
                                        Added to Register
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="px-2.5 py-0.5 rounded-lg text-[10px] font-bold text-gray-500 border-gray-200">
                                        Not Required
                                    </Badge>
                                )}
                            </div>
                            {asset.addToLiftingRegister && (
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Inspection Cadence</span>
                                    {asset.liftingCadenceNumber ? (
                                        <p className="text-sm font-bold text-gray-900">
                                            Every {asset.liftingCadenceNumber} {asset.liftingCadenceUnit}
                                        </p>
                                    ) : (
                                        <p className="text-sm font-medium text-gray-500 italic">
                                            Not specified
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Type-Specific Details (Custom Fields) */}
                    {asset.customFields && Object.keys(asset.customFields).length > 0 && (
                        <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3">
                                <Box size={20} className="text-indigo-500" />
                                <div>
                                    <h3 className="font-bold text-gray-900 text-base">Type-Specific Details</h3>
                                    <p className="text-gray-500 text-xs font-medium">Additional information for {asset.type}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-12">
                                {Object.entries(asset.customFields).map(([key, value]) => (
                                    <div key={key} className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">{key}</span>
                                        <p className="text-sm font-bold text-gray-900">
                                            {value !== undefined && value !== null && value !== '' ? String(value) : '-'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Inspection Details */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <ClipboardCheck size={20} className="text-blue-500" />
                            <div>
                                <h3 className="font-bold text-gray-900 text-base">Inspection Details</h3>
                                <p className="text-gray-500 text-xs font-medium">Routine inspection tracking and cadence</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Cadence</span>
                                {asset.inspectionCadenceNumber ? (
                                    <p className="text-sm font-bold text-gray-900">
                                        Every {asset.inspectionCadenceNumber} {asset.inspectionCadenceUnit}
                                    </p>
                                ) : (
                                    <p className="text-sm font-medium text-gray-500 italic">
                                        Inspection cadence has not been setup.
                                    </p>
                                )}
                            </div>
                            {asset.nextInspectionDue && (
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Next Due Date</span>
                                    <p className="text-sm font-bold text-blue-600">
                                        {toSydneyDate(asset.nextInspectionDue)}
                                    </p>
                                    {new Date(asset.nextInspectionDue) < new Date() && (
                                        <Badge variant="destructive" className="mt-1 px-2 py-0 text-[9px] uppercase tracking-wider font-bold">Overdue</Badge>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Status Management Card */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3">
                            <Activity size={20} className="text-blue-500" />
                            <div>
                                <h3 className="font-bold text-gray-900 text-base">Status Management</h3>
                                <p className="text-gray-500 text-xs font-medium">Update asset status following workflow rules</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_STATUS) ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <div className="p-1 bg-gray-50 rounded-xl w-full flex items-center cursor-pointer hover:bg-gray-100 transition-colors group">
                                            <Button variant="ghost" className="flex-1 justify-start rounded-lg h-9 text-xs font-bold gap-2 bg-white text-blue-600 shadow-sm border border-gray-100">
                                                {asset.status === 'Assigned' ? <Play size={14} /> :
                                                    asset.status === 'Available' ? <CheckCircle2 size={14} /> :
                                                        asset.status === 'Needs Repair' ? <AlertTriangle size={14} /> :
                                                            <Activity size={14} />}
                                                {asset.status}
                                            </Button>
                                            <div className="px-3">
                                                <MoreHorizontal className="text-gray-300 group-hover:text-gray-500 transition-colors" size={18} />
                                            </div>
                                        </div>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="start"
                                        sideOffset={8}
                                        className="w-[320px] rounded-2xl p-0 border-gray-100 shadow-xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[85vh] md:max-h-[500px]"
                                    >
                                        <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-50 flex items-center gap-2 shrink-0">
                                            <Info size={14} className="text-gray-400" />
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Valid transitions shown</span>
                                        </div>

                                        <div className="p-2 space-y-1 overflow-y-auto custom-scrollbar flex-1 min-h-0">
                                            {Object.entries(getValidTransitions(asset.status)).map(([section, items], index, array) => (
                                                <React.Fragment key={section}>
                                                    <div className="px-2 py-2">
                                                        <Badge variant="outline" className={cn(
                                                            "text-[9px] font-black uppercase tracking-tight rounded-md border-0 py-0 px-1.5",
                                                            section === 'Workflow' ? "bg-blue-50 text-blue-600" :
                                                                section === 'Maintenance' ? "bg-amber-50 text-amber-600" :
                                                                    section === 'Issues' ? "bg-red-50 text-red-600" :
                                                                        "bg-slate-100 text-slate-600"
                                                        )}>
                                                            {section}
                                                        </Badge>
                                                    </div>
                                                    {items.map((item) => (
                                                        <DropdownMenuItem
                                                            key={item.id}
                                                            onClick={() => handleStatusUpdate(item.id as AssetStatus)}
                                                            className={cn(
                                                                "flex flex-col items-start gap-0.5 p-3 rounded-xl cursor-pointer transition-all",
                                                                asset.status === item.id ? "bg-blue-50/50" : "hover:bg-gray-50"
                                                            )}
                                                        >
                                                            <div className="flex items-center justify-between w-full">
                                                                <div className="flex items-center gap-3">
                                                                    <div className={cn("text-gray-400", asset.status === item.id && "text-blue-600")}>
                                                                        {item.icon}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={cn("text-sm font-bold", asset.status === item.id ? "text-blue-600" : "text-gray-700")}>
                                                                            {item.label}
                                                                        </span>
                                                                        {asset.status === item.id && (
                                                                            <Badge className="bg-blue-100 text-blue-600 hover:bg-blue-100 border-0 text-[10px] font-bold py-0 h-4">
                                                                                Current
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {item.id === 'Available' && <div className="w-2.5 h-2.5 rounded-full bg-green-400 opacity-20" />}
                                                                {item.id === 'Assigned' && <div className="w-2.5 h-2.5 rounded-full bg-blue-400 opacity-20" />}
                                                                {item.id === 'Needs Repair' && <div className="w-2.5 h-2.5 rounded-full bg-red-400 opacity-20" />}
                                                                {item.id === 'Lost' && <div className="w-2.5 h-2.5 rounded-full bg-slate-900 opacity-20" />}
                                                                {item.id === 'Retired' && <div className="w-2.5 h-2.5 rounded-full bg-gray-500 opacity-20" />}
                                                                {item.id === 'Disposed' && <div className="w-2.5 h-2.5 rounded-full bg-gray-500 opacity-20" />}
                                                            </div>
                                                            <p className="text-[11px] font-medium text-gray-400 pl-8 leading-tight">
                                                                {item.description}
                                                            </p>
                                                        </DropdownMenuItem>
                                                    ))}
                                                    {index < array.length - 1 && <DropdownMenuSeparator className="my-1 bg-gray-50" />}
                                                </React.Fragment>
                                            ))}
                                        </div>

                                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-50 space-y-1 shrink-0 rounded-b-2xl">
                                            <div className="flex items-center gap-2">
                                                <Info size={14} className="text-gray-400" />
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Workflow Note</span>
                                            </div>
                                            <p className="text-[10px] font-medium text-gray-400 leading-normal">
                                                Status transitions follow business rules. Blocked transitions are hidden from this menu.
                                            </p>
                                        </div>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <div className="p-1 bg-gray-50 rounded-xl w-full flex items-center">
                                    <div className="flex-1 flex items-center rounded-lg h-9 text-xs font-bold gap-2 px-4 bg-white text-gray-600 shadow-sm border border-gray-100">
                                        {asset.status === 'Assigned' ? <Play size={14} /> :
                                            asset.status === 'Available' ? <CheckCircle2 size={14} /> :
                                                asset.status === 'Needs Repair' ? <AlertTriangle size={14} /> :
                                                    <Activity size={14} />}
                                        {asset.status}
                                    </div>
                                </div>
                            )}

                            <div className="p-6 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-gray-700">Current Status:</span>
                                    <span className="text-sm font-medium text-gray-600">
                                        {asset.status === 'Assigned' ? 'Currently assigned and in use' :
                                            asset.status === 'Available' ? 'Ready to be assigned' : 'Requires attention'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-gray-700">Trigger:</span>
                                    <span className="text-sm font-medium text-gray-600">
                                        {asset.status === 'Assigned' ? 'Currently assigned and in use' : 'No active assignment'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Tabs Section */}
                    <div className="space-y-4">
                        {/* Moved tabOptions and activeTabOption definition outside the Tabs component */}
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <div className="flex items-center justify-between mb-4">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="bg-gray-100/50 border-gray-100 hover:bg-white transition-all rounded-2xl h-11 px-4 text-xs font-bold gap-2">
                                            <LayoutGrid size={16} className="text-gray-400" />
                                            <span className="text-blue-600 font-bold">{activeTabOption?.label}</span>
                                            {activeTabOption && activeTabOption.count > 0 && (
                                                <Badge className="bg-blue-100 text-blue-600 border-0 text-[10px] font-bold py-0 h-4 min-w-[1.25rem] flex items-center justify-center">
                                                    {activeTabOption.count}
                                                </Badge>
                                            )}
                                            <ChevronDown size={14} className="text-gray-400 ml-2" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="w-[240px] p-2 rounded-2xl">
                                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-gray-400 font-bold px-3 py-2">Select View</DropdownMenuLabel>
                                        <DropdownMenuSeparator className="bg-gray-50 mx-2" />
                                        {tabOptions.map((tab) => (
                                            <DropdownMenuItem
                                                key={tab.id}
                                                className={cn(
                                                    "rounded-xl px-3 py-2.5 text-xs font-bold cursor-pointer mb-1 last:mb-0 transition-all",
                                                    activeTab === tab.id ? "bg-blue-50 text-blue-600" : "text-gray-600 hover:bg-gray-50"
                                                )}
                                                onClick={() => setActiveTab(tab.id)}
                                            >
                                                <div className="flex items-center justify-between w-full">
                                                    <span>{tab.label}</span>
                                                    {tab.count > 0 && (
                                                        <Badge className={cn(
                                                            "text-[10px] font-bold py-0 h-4 border-0 min-w-[1.25rem] flex items-center justify-center",
                                                            activeTab === tab.id ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-400"
                                                        )}>
                                                            {tab.count}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>

                            <TabsContent value="assignments">
                                <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-base">Assignment History</h3>
                                        <p className="text-gray-500 text-xs font-medium">Track who has been assigned this asset over time</p>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-gray-50">
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Assigned To</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Assigned By</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date Assigned</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                                    <th className="text-right py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {history.assignments.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} className="py-10 text-center text-gray-400 font-medium italic">No assignment history found.</td>
                                                    </tr>
                                                ) : (
                                                    history.assignments.map((record) => (
                                                        <tr key={record.id} className="group hover:bg-gray-50/50 transition-colors">
                                                            <td className="py-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 font-bold text-xs uppercase">
                                                                        {record.assignedToUser ? <User size={14} /> : <Building2 size={14} />}
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-sm font-bold text-gray-900">
                                                                            {record.assignedToUser?.name || record.assignedToDepartment?.departmentName || 'Unknown'}
                                                                        </p>
                                                                        <p className="text-[10px] font-medium text-gray-400">
                                                                            {record.assignedToUser?.jobRole || 'Department'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="py-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-6 h-6 rounded-lg bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-500 uppercase">
                                                                        {record.assignedBy?.name?.charAt(0) || 'S'}
                                                                    </div>
                                                                    <span className="text-xs font-bold text-gray-700">{record.assignedBy?.name || 'System'}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-4 text-xs font-bold text-gray-700">
                                                                {toSydneyDate(record.assignedDate)}
                                                            </td>
                                                            <td className="py-4">
                                                                <Badge className={cn("px-2 py-0.5 rounded-lg border-0 text-[9px] font-black uppercase tracking-tight", record.isActive ? "bg-slate-900 text-white" : "bg-gray-100 text-gray-400")}>
                                                                    {record.isActive ? (record.returnedDate ? 'Returned' : 'Active') : 'Completed'}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-4 text-right">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                                                    onClick={() => navigate(`/assets/${asset.id}/assignments/${record.id}`)}
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={16} />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </TabsContent>

                            <TabsContent value="maintenance">
                                <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-base">Maintenance History</h3>
                                        <p className="text-gray-500 text-xs font-medium">Log of all maintenance activities</p>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-gray-50">
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-4">Date</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Type</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Technician</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Vendor</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Cost</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Notes</th>
                                                    <th className="text-right py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pr-4">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {!history.maintenance || history.maintenance.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={8} className="py-10 text-center text-gray-400 font-medium italic">No maintenance history found.</td>
                                                    </tr>
                                                ) : (
                                                    history.maintenance.map((record: any) => (
                                                        <tr key={record.id} className="group hover:bg-gray-50/50 transition-colors">
                                                            <td className="py-4 pl-4 text-xs font-bold text-gray-700">
                                                                {record.completedDate ?
                                                                    toSydneyDate(record.completedDate) :
                                                                    toSydneyDate(record.scheduledDate)
                                                                }
                                                            </td>
                                                            <td className="py-4 text-xs font-bold text-gray-700">{record.type}</td>
                                                            <td className="py-4">
                                                                <span className="text-xs font-bold text-gray-700">{record.performedBy || '-'}</span>
                                                            </td>
                                                            <td className="py-4 text-xs font-medium text-gray-700">{record.vendor || '-'}</td>
                                                            <td className="py-4 text-xs font-medium text-gray-700">{record.cost ? `$${record.cost}` : '-'}</td>
                                                            <td className="py-4">
                                                                <Badge className={cn("px-2 py-0.5 rounded-lg border-0 text-[10px] font-bold",
                                                                    record.status === 'Completed' ? "bg-green-100 text-green-700" :
                                                                        record.status === 'Scheduled' ? "bg-slate-100 text-slate-700" :
                                                                            "bg-blue-100 text-blue-700"
                                                                )}>
                                                                    {record.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-4">
                                                                <p className="text-xs font-medium text-gray-500 line-clamp-2 max-w-[200px]">
                                                                    {record.description || '-'}
                                                                </p>
                                                            </td>
                                                            <td className="py-4 pr-4 pl-4 text-right">
                                                                <div className="flex items-center justify-end gap-2">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                                                        onClick={() => navigate(`/assets/${asset.id}/maintenance/${record.id}`)}
                                                                    >
                                                                        <Eye size={16} />
                                                                    </Button>
                                                                    {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_MAINTENANCE) && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                                                                            onClick={() => resetMaintenanceModal(record, 'edit')}
                                                                            title="Edit Details"
                                                                        >
                                                                            <Edit size={16} />
                                                                        </Button>
                                                                    )}
                                                                    {record.status === 'Scheduled' && ability.can(ACTIONS.COMPLETE, RESOURCES.ASSET_MAINTENANCE) && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 text-green-500 hover:text-green-700 hover:bg-green-50"
                                                                            onClick={() => resetMaintenanceModal(record, 'complete')}
                                                                            title="Complete Maintenance"
                                                                        >
                                                                            <CheckCircle2 size={16} />
                                                                        </Button>
                                                                    )}
                                                                    {ability.can(ACTIONS.DELETE, RESOURCES.ASSET_MAINTENANCE) && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                                            onClick={() => handleDeleteMaintenance(record.id)}
                                                                            title="Delete Record"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </TabsContent>


                            <TabsContent value="repairs">
                                {ability.can(ACTIONS.READ, RESOURCES.ASSET_REPAIR) && (
                                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-base">Repair History</h3>
                                            <p className="text-gray-500 text-xs font-medium">Log of all recorded repairs for this asset</p>
                                        </div>

                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b border-gray-50">
                                                        <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-4">Date</th>
                                                        <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Vendor</th>
                                                        <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Parts Repaired</th>
                                                        <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Cost</th>
                                                        <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                                        <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Description</th>
                                                        <th className="text-right py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pr-4">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50">
                                                    {!history.repairs || history.repairs.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={6} className="py-10 text-center text-gray-400 font-medium italic">No repair records found.</td>
                                                        </tr>
                                                    ) : (
                                                        history.repairs.map((record: any) => (
                                                            <tr key={record.id} className="group hover:bg-gray-50/50 transition-colors">
                                                                <td className="py-4 pl-4 text-xs font-bold text-gray-700">
                                                                    {toSydneyDate(record.repairDate)}
                                                                </td>
                                                                <td className="py-4 text-xs font-bold text-gray-700">{record.vendor}</td>
                                                                <td className="py-4 text-xs font-medium text-gray-600 truncate max-w-[150px]">{record.partsRepaired}</td>
                                                                <td className="py-4 text-xs font-bold text-gray-700">${Number(record.cost).toLocaleString()}</td>
                                                                <td className="py-4">
                                                                    <Badge className={cn("px-2 py-0.5 rounded-lg border-0 text-[10px] font-bold",
                                                                        record.status === 'Completed' ? "bg-green-100 text-green-700" :
                                                                            record.status === 'Pending Approval' ? "bg-amber-100 text-amber-700" :
                                                                                "bg-blue-100 text-blue-700"
                                                                    )}>
                                                                        {record.status}
                                                                    </Badge>
                                                                </td>
                                                                <td className="py-4">
                                                                    <p className="text-xs font-medium text-gray-500 line-clamp-2 max-w-[200px]">
                                                                        {record.description || '-'}
                                                                    </p>
                                                                </td>
                                                                <td className="py-4 pr-4 pl-4 text-right">
                                                                    <div className="flex justify-end gap-1">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                                                            onClick={() => navigate(`/assets/${asset.id}/repair/${record.id}`)}
                                                                            title="View Details"
                                                                        >
                                                                            <Eye size={16} />
                                                                        </Button>
                                                                        {(record.status === 'Pending Approval'
                                                                            ? ability.can(ACTIONS.UPDATE_PENDING_REPAIR, RESOURCES.ASSET_REPAIR)
                                                                            : ability.can(ACTIONS.UPDATE_APPROVED_REPAIR, RESOURCES.ASSET_REPAIR)) && (
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                                                                    onClick={() => navigate(`/assets/${asset.id}/repair/${record.id}/edit`)}
                                                                                    title="Edit Repair"
                                                                                >
                                                                                    <Edit size={16} />
                                                                                </Button>
                                                                            )}
                                                                        {(record.status === 'Pending Approval'
                                                                            ? ability.can(ACTIONS.DELETE_PENDING_REPAIR, RESOURCES.ASSET_REPAIR)
                                                                            : ability.can(ACTIONS.DELETE_APPROVED_REPAIR, RESOURCES.ASSET_REPAIR)) && (
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="h-8 w-8 p-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
                                                                                    onClick={() => handleDeleteRepair(record.id)}
                                                                                    title="Delete Repair"
                                                                                >
                                                                                    <Trash2 size={16} />
                                                                                </Button>
                                                                            )}

                                                                        {record.status === 'Pending Approval' && record.approvedBy?.id && currentUser?.id && String(record.approvedBy.id) === String(currentUser.id) && ability.can(ACTIONS.APPROVE, RESOURCES.ASSET_REPAIR) && (
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-8 w-8 p-0 text-green-500 hover:text-green-700 hover:bg-green-50"
                                                                                onClick={async () => {
                                                                                    try {
                                                                                        await api.patch(`/api/assets/${asset.id}/repair/${record.id}/approve`, {});
                                                                                        toast.success('Repair approved successfully');
                                                                                        fetchData();
                                                                                    } catch (err: any) {
                                                                                        toast.error(err?.message || 'Failed to approve repair');
                                                                                    }
                                                                                }}
                                                                                title="Approve Repair"
                                                                            >
                                                                                <CheckCircle2 size={16} />
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </section>
                                )}
                            </TabsContent>

                            <TabsContent value="inspections">
                                <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-base">Inspection History</h3>
                                        <p className="text-gray-500 text-xs font-medium">Log of all routine and safety inspections</p>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-gray-50">
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-4">Date</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Template</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Inspector</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Outcome</th>
                                                    <th className="text-right py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pr-4">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {!history.inspections || history.inspections.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} className="py-10 text-center text-gray-400 font-medium italic">No inspection history found.</td>
                                                    </tr>
                                                ) : (
                                                    history.inspections.map((record: any) => (
                                                        <tr key={record.id} className="group hover:bg-gray-50/50 transition-colors">
                                                            <td className="py-4 pl-4 text-xs font-bold text-gray-700">
                                                                {toSydneyDate(record.inspectionDate)}
                                                            </td>
                                                            <td className="py-4 text-xs font-bold text-gray-700">{record.template?.name || '-'}</td>
                                                            <td className="py-4">
                                                                <span className="text-xs font-bold text-gray-700">{record.inspectorName || 'Unknown'}</span>
                                                            </td>
                                                            <td className="py-4">
                                                                <Badge className={cn("px-2 py-0.5 rounded-lg border-0 text-[10px] font-bold",
                                                                    record.status === 'Completed' ? "bg-green-100 text-green-700" :
                                                                        record.status === 'Draft' ? "bg-amber-100 text-amber-700" :
                                                                            "bg-blue-100 text-blue-700"
                                                                )}>
                                                                    {record.status === 'Draft' ? 'In Draft' : record.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-4">
                                                                {record.status === 'Draft' ? (
                                                                    <span className="text-xs font-bold text-gray-400">-</span>
                                                                ) : (
                                                                    <span className={cn("text-xs font-bold",
                                                                        record.outcome === 'Pass with Notes' ? "text-amber-600" :
                                                                            record.outcome === 'Fail' ? "text-red-600" :
                                                                                record.outcome === 'Pass' ? "text-emerald-600" :
                                                                                    "text-gray-700"
                                                                    )}>
                                                                        {record.outcome || '-'}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="py-4 pr-4 text-right">
                                                                {record.status === 'Draft' && (
                                                                    <div className="flex justify-end gap-2">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                                                                            onClick={() => navigate(`/assets/${asset.id}/inspect?draft=${record.id}`)}
                                                                            title="Continue Inspection"
                                                                        >
                                                                            <ArrowRight size={16} />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                                                                            onClick={() => handleDeleteDraft(String(record.id))}
                                                                            title="Delete Draft"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                                {record.status === 'Completed' && (
                                                                    <div className="flex justify-end gap-2">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                                                            onClick={() => navigate(`/assets/${asset.id}/inspections/${record.id}`)}
                                                                            title="View Inspection"
                                                                        >
                                                                            <Eye size={16} />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </TabsContent>

                            <TabsContent value="reminders">
                                <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-base">Asset Reminders</h3>
                                            <p className="text-gray-500 text-xs font-medium">Scheduled notifications for this asset</p>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-gray-50">
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-4">Event Name</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Next Due</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Recurrence</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Recipients</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                                    <th className="text-right py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pr-4">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {!history.reminders || history.reminders.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} className="py-10 text-center text-gray-400 font-medium italic">No reminders found.</td>
                                                    </tr>
                                                ) : (
                                                    history.reminders.map((reminder) => (
                                                        <tr key={reminder.id} className="group hover:bg-gray-50/50 transition-colors">
                                                            <td className="py-4 pl-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                                                                        <Bell size={14} />
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-sm font-bold text-gray-900">{reminder.eventName}</p>
                                                                        <p className="text-[10px] font-medium text-gray-400">{reminder.eventType}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="py-4 text-xs font-bold text-gray-700">
                                                                {toSydneyDate(reminder.nextDueDate)}
                                                            </td>
                                                            <td className="py-4 text-xs font-medium text-gray-600">
                                                                {reminder.recurrencePattern}
                                                            </td>
                                                            <td className="py-4">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {reminder.notifyAssignee && (
                                                                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded bg-gray-50">Assignee</Badge>
                                                                    )}
                                                                    {reminder.recipients?.map(r => (
                                                                        <Badge key={r.id} variant="outline" className="text-[9px] px-1.5 py-0 rounded">{r.user?.name}</Badge>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="py-4">
                                                                <Badge className={cn("px-2 py-0.5 rounded-lg border-0 text-[10px] font-bold",
                                                                    reminder.status === 'Active' ? "bg-green-100 text-green-700" :
                                                                        reminder.status === 'Completed' ? "bg-blue-100 text-blue-700" :
                                                                            "bg-gray-100 text-gray-400"
                                                                )}>
                                                                    {reminder.status}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-4 pr-4 text-right">
                                                                <div className="flex justify-end gap-2 text-right">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0 text-gray-400 hover:bg-gray-50 hover:text-blue-600"
                                                                        onClick={() => navigate(`/assets/${id}/reminders/${reminder.id}/edit`)}
                                                                        title="Edit Reminder"
                                                                    >
                                                                        <Edit size={16} />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0 text-gray-400 hover:bg-gray-50 hover:text-red-500"
                                                                        onClick={() => handleDeleteReminder(reminder.id)}
                                                                        title="Delete Reminder"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </TabsContent>

                            <TabsContent value="documents">
                                <AssetDocuments asset={asset} />
                            </TabsContent>

                            <TabsContent value="history">
                                <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-base">Status History</h3>
                                        <p className="text-gray-500 text-xs font-medium">Log of all status changes for this asset</p>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b border-gray-50">
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-4">Date</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Transition</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Trigger</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">By</th>
                                                    <th className="text-left py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest pr-4">Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {!history.statusChanges || history.statusChanges.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="py-10 text-center text-gray-400 font-medium italic">No status history found.</td>
                                                    </tr>
                                                ) : (
                                                    history.statusChanges.map((record) => (
                                                        <tr key={record.id} className="group hover:bg-gray-50/50 transition-colors">
                                                            <td className="py-4 pl-4 text-xs font-bold text-gray-700">
                                                                {toSydneyDate(record.createdAt)}
                                                                <span className="block text-[10px] text-gray-400 font-medium mt-0.5">
                                                                    {formatSydneyTime(record.createdAt)}
                                                                </span>
                                                            </td>
                                                            <td className="py-4">
                                                                <div className="flex items-center gap-2">
                                                                    {record.fromStatus && record.fromStatus !== 'None' ? (
                                                                        <>
                                                                            <Badge variant="outline" className={cn("px-2 py-0.5 rounded-lg text-[10px] font-bold", getStatusStyles(record.fromStatus as AssetStatus))}>
                                                                                {record.fromStatus}
                                                                            </Badge>
                                                                            <ArrowRight size={14} className="text-gray-300" />
                                                                        </>
                                                                    ) : null}
                                                                    <Badge variant="outline" className={cn("px-2 py-0.5 rounded-lg text-[10px] font-bold", getStatusStyles(record.toStatus as AssetStatus))}>
                                                                        {record.toStatus}
                                                                    </Badge>
                                                                </div>
                                                            </td>
                                                            <td className="py-4">
                                                                <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px] font-bold capitalize">
                                                                    {record.triggeredByType || 'Manual'}
                                                                </Badge>
                                                            </td>
                                                            <td className="py-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-500 uppercase">
                                                                        {record.createdBy?.name?.charAt(0) || 'S'}
                                                                    </div>
                                                                    <span className="text-xs font-bold text-gray-700">{record.createdBy?.name || 'System'}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-4 pr-4">
                                                                <p className="text-xs font-medium text-gray-500 line-clamp-2 max-w-[200px]">
                                                                    {record.notes || '-'}
                                                                </p>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            </TabsContent>
                            <TabsContent value="notes">
                                <div className="space-y-6">
                                    {/* Add New Note Section */}
                                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-4">
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-base">Add New Note</h3>
                                            <p className="text-gray-500 text-xs font-medium">Document important information, observations, or updates about this asset</p>
                                        </div>
                                        <div className="space-y-3">
                                            <textarea
                                                className="w-full min-h-[100px] p-4 rounded-2xl border border-gray-100 bg-gray-50/50 text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400 resize-none"
                                                placeholder="Type your note here..."
                                                value={newNoteText}
                                                onChange={(e) => setNewNoteText(e.target.value)}
                                            />
                                            <div className="flex justify-end">
                                                <Button
                                                    className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs gap-2 flex items-center px-4 md:px-6 h-10 shadow-sm"
                                                    onClick={handleAddNote}
                                                    disabled={notesLoading || !newNoteText.trim()}
                                                >
                                                    {notesLoading ? 'Adding...' : (
                                                        <>
                                                            <Plus size={14} />
                                                            <span className="hidden md:inline">Add Note</span>
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </section>

                                    {/* Notes Timeline Section */}
                                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-base">Notes Timeline</h3>
                                            <p className="text-gray-500 text-xs font-medium">History of all notes added to this asset</p>
                                        </div>

                                        <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gray-50">
                                            {!history.notes || history.notes.length === 0 ? (
                                                <div className="pl-12 py-4">
                                                    <p className="text-sm font-medium text-gray-400 italic">No notes found for this asset.</p>
                                                </div>
                                            ) : (
                                                history.notes.map((note) => (
                                                    <div key={note.id} className="relative pl-12">
                                                        {/* Timeline Dot */}
                                                        <div className="absolute left-0 top-1 w-10 h-10 rounded-full bg-white border-4 border-white flex items-center justify-center shadow-sm z-10">
                                                            <div className="w-full h-full rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                                                                <FileText size={16} />
                                                            </div>
                                                        </div>

                                                        <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-50 group hover:border-blue-100 hover:bg-blue-50/30 transition-all">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-6 h-6 rounded-lg bg-white border border-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-400 uppercase">
                                                                        {note.createdBy?.name?.charAt(0) || 'U'}
                                                                    </div>
                                                                    <span className="text-xs font-bold text-gray-900">{note.createdBy?.name || 'Unknown User'}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 text-gray-400">
                                                                    <Clock size={12} />
                                                                    <span className="text-[10px] font-bold uppercase tracking-wider">
                                                                        {toSydneyDate(note.createdAt)} at {formatSydneyTime(note.createdAt)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <p className="text-sm text-gray-600 font-medium leading-relaxed whitespace-pre-wrap">
                                                                {note.text}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </section>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>

                {/* Sidebar Column */}
                <div className="space-y-6">
                    {/* Asset Information Card */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <h3 className="font-bold text-gray-900 text-base">Asset Information</h3>

                        <div className="w-full aspect-video bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 shadow-inner overflow-hidden">
                            {asset.image ? (
                                <img
                                    src={asset.image}
                                    alt={asset.name}
                                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                    onClick={() => setIsImageModalOpen(true)}
                                />
                            ) : (
                                <Package size={64} className="text-gray-200" />
                            )}
                        </div>

                        <div className="space-y-6">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Purchase Date</span>
                                <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                                    <CalendarIcon className="text-blue-500" size={16} />
                                    {asset.purchaseDate ? toSydneyDate(asset.purchaseDate) : 'Not set'}
                                </div>
                            </div>
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                <div className="space-y-1">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Purchase Price</span>
                                    <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                                        <DollarSign className="text-emerald-500" size={16} />
                                        ${Number(asset.purchasePrice || 0).toLocaleString()}
                                    </div>
                                </div>
                            )}
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Location</span>
                                <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                                    <MapPin className="text-orange-500" size={16} />
                                    {asset.location || 'Not specified'}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Organisation</span>
                                <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                                    <Building2 className="text-violet-500" size={16} />
                                    {asset.organization?.companyName || 'Not specified'}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Current Assignment Card */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <h3 className="font-bold text-gray-900 text-base">Current Assignment</h3>

                        <div className="flex items-start gap-4 p-4 bg-gray-50/50 rounded-2xl border border-gray-50">
                            <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400 shadow-sm">
                                {asset.assignedToUser || asset.assignedToDepartment ? <User size={20} /> : <AlertCircle size={20} />}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-gray-900">
                                    {(asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName) || 'Currently Unassigned'}
                                </p>
                                <p className="text-[10px] font-medium text-gray-400 mt-0.5 uppercase tracking-wider">
                                    {asset.assignedToUser || asset.assignedToDepartment ? 'Since ' + toSydneyDate(new Date()) : 'Available for assignment'}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Quick Actions Card */}
                    <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <h3 className="font-bold text-gray-900 text-base">Quick Actions</h3>

                        <div className="space-y-2">
                            {(hasInspectionTemplate || asset.addToElectricalRegister || asset.addToLiftingRegister) && ability.can(ACTIONS.CREATE, RESOURCES.ASSET_INSPECTION) && (
                                <>
                                    {hasInspectionTemplate && (
                                        <Button
                                            variant="outline"
                                            className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                            onClick={() => navigate(`/assets/${asset.id}/inspect`)}
                                        >
                                            <ClipboardCheck size={18} className="text-gray-400" />
                                            Start New Inspection
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center text-blue-700 hover:text-blue-800"
                                        onClick={() => setIsQRModalOpen(true)}
                                    >
                                        <QrCode size={18} className="text-blue-500" />
                                        Print Inspection QR
                                    </Button>
                                </>
                            )}
                            {asset.addToElectricalRegister && ability.can(ACTIONS.CREATE, RESOURCES.ASSET_INSPECTION) && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                    onClick={() => navigate(`/assets/electrical-inspections/new?assetId=${asset.id}`)}
                                >
                                    <Zap size={18} className="text-amber-500" />
                                    Start Electrical Inspection
                                </Button>
                            )}
                            {asset.addToLiftingRegister && ability.can(ACTIONS.CREATE, RESOURCES.ASSET_INSPECTION) && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                    onClick={() => navigate(`/assets/lifting-inspections/new?assetId=${asset.id}`)}
                                >
                                    <Anchor size={18} className="text-orange-500" />
                                    Start Lifting Inspection
                                </Button>
                            )}
                            {ability.can(ACTIONS.CREATE, RESOURCES.ASSET_MAINTENANCE) && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                    onClick={() => navigate(`/assets/${asset.id}/maintenance/new`)}
                                >
                                    <Settings size={18} className="text-gray-400" />
                                    Schedule Maintenance
                                </Button>
                            )}
                            {ability.can(ACTIONS.CREATE, RESOURCES.ASSET_REPAIR) && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                    onClick={() => navigate(`/assets/${asset.id}/repair/new`)}
                                >
                                    <Wrench size={18} className="text-gray-400" />
                                    Record Repair
                                </Button>
                            )}
                            {ability.can(ACTIONS.CREATE, RESOURCES.ASSET_REMINDER) && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                    onClick={() => navigate(`/assets/${id}/reminders/new`)}
                                >
                                    <Bell size={18} className="text-gray-400" />
                                    Add Reminder
                                </Button>
                            )}
                            {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET_ASSIGNMENT) && (
                                (asset.status === 'Assigned' || asset.assignedToUser || asset.assignedToDepartment) ? (
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                        onClick={() => navigate(`/assets/${asset.id}/return`, { state: { targetStatus: 'In Triage' } })}
                                    >
                                        <ArrowLeft size={18} className="text-gray-400" />
                                        Return Asset
                                    </Button>
                                ) : (asset.status === 'Available') ? (
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                        onClick={() => navigate('/assets/assign', { state: { preSelectedAsset: asset.id.toString() } })}
                                    >
                                        <User size={18} className="text-gray-400" />
                                        Assign Asset
                                    </Button>
                                ) : null
                            )}
                            {ability.can(ACTIONS.UPDATE, RESOURCES.ASSET) && (
                                <Button
                                    variant="outline"
                                    className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center"
                                    onClick={() => navigate(`/assets/${asset.id}/edit`)}
                                >
                                    <Edit size={18} className="text-gray-400" />
                                    Edit Details
                                </Button>
                            )}
                            {ability.can(ACTIONS.ARCHIVE, RESOURCES.ASSET) && asset.status !== 'Archived' && (
                                <Button variant="outline" className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center text-gray-700" onClick={handleArchiveClick}>
                                    <Archive size={18} className="text-gray-400" />
                                    Archive Asset
                                </Button>
                            )}
                            {ability.can(ACTIONS.ARCHIVE, RESOURCES.ASSET) && asset.status === 'Archived' && (
                                <Button variant="outline" className="w-full justify-start h-12 rounded-2xl border-blue-50 hover:bg-blue-50 bg-white font-bold text-xs gap-3 flex items-center text-blue-600" onClick={() => setShowRestoreModal(true)}>
                                    <History size={18} className="text-blue-500" />
                                    Restore Asset
                                </Button>
                            )}
                            <Button variant="outline" className="w-full justify-start h-12 rounded-2xl border-gray-50 hover:bg-gray-50 bg-white font-bold text-xs gap-3 flex items-center">
                                <div className="flex items-center gap-3 flex-1">
                                    <Download size={18} className="text-gray-400" />
                                    <span>Download Report</span>
                                </div>
                                <Badge variant="secondary" className="bg-gray-100 text-gray-500 hover:bg-gray-100 border-none text-[10px] px-2 py-0.5 rounded-lg ml-auto">
                                    Coming soon
                                </Badge>
                            </Button>
                        </div>
                    </section>
                </div>
            </div>

            <AlertDialog open={showAssignModal} onOpenChange={setShowAssignModal}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Assign Asset</AlertDialogTitle>
                        <AlertDialogDescription>
                            Changing the status to Active requires assigning the asset to a user or department. You will be redirected to the Assign Assets page to complete this process.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
                            onClick={() => {
                                setShowAssignModal(false);
                                navigate('/assets/assign', { state: { preSelectedAsset: asset?.id.toString() } });
                            }}
                        >
                            Confirm Redirect
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showUnassignModal} onOpenChange={setShowUnassignModal}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unassign Asset</AlertDialogTitle>
                        <AlertDialogDescription>
                            Changing the status to <strong>{pendingStatus || 'this state'}</strong> will automatically remove the currently assigned user or department and close the active assignment record. Are you sure you want to continue?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold"
                            onClick={async () => {
                                setShowUnassignModal(false);
                                if (pendingStatus) {
                                    await processStatusUpdate(pendingStatus);
                                    setPendingStatus(null);
                                }
                            }}
                        >
                            Confirm Unassign
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showFinalStatusModal} onOpenChange={setShowFinalStatusModal}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Final Status Change</AlertDialogTitle>
                        <AlertDialogDescription>
                            Changing the status to <strong>{pendingStatus}</strong> is permanent and cannot be reversed. Any active assignment will be automatically set to Completed and removed. Are you sure you want to proceed?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                            onClick={async () => {
                                setShowFinalStatusModal(false);
                                if (pendingStatus) {
                                    await processStatusUpdate(pendingStatus);
                                    setPendingStatus(null);
                                }
                            }}
                        >
                            Confirm And Finalize
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showArchiveAlert.isOpen} onOpenChange={(isOpen) => setShowArchiveAlert(prev => ({ ...prev, isOpen }))}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Archive Asset</AlertDialogTitle>
                        <AlertDialogDescription>
                            {showArchiveAlert.message}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        {showArchiveAlert.canArchive ? (
                            <>
                                <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                                    onClick={async () => {
                                        setShowArchiveAlert(prev => ({ ...prev, isOpen: false }));
                                        await processStatusUpdate('Archived');
                                    }}
                                >
                                    Confirm Archive
                                </AlertDialogAction>
                            </>
                        ) : (
                            <AlertDialogAction
                                className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
                                onClick={() => setShowArchiveAlert(prev => ({ ...prev, isOpen: false }))}>
                                OK
                            </AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showRestoreModal} onOpenChange={setShowRestoreModal}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Restore Asset</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to restore this asset? It will be marked as Available.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
                            onClick={async () => {
                                setShowRestoreModal(false);
                                await processStatusUpdate('Available');
                            }}
                        >
                            Confirm Restore
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDeleteReminderAlert} onOpenChange={setShowDeleteReminderAlert}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle size={20} />
                            Delete Reminder
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this reminder? This action cannot be undone and you will no longer receive notifications for this event.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                            onClick={confirmDeleteReminder}
                        >
                            Confirm Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDeleteDraftAlert} onOpenChange={setShowDeleteDraftAlert}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle size={20} />
                            Delete Inspection Draft
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this inspection draft? This action cannot be undone and the draft will be removed from the asset.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                            onClick={confirmDeleteDraft}
                        >
                            Confirm Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDeleteMaintenanceAlert} onOpenChange={setShowDeleteMaintenanceAlert}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle size={20} />
                            Delete Maintenance Record
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this maintenance record? This action cannot be undone and will be removed from the asset history.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                            onClick={confirmDeleteMaintenance}
                        >
                            Confirm Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDeleteRepairAlert} onOpenChange={setShowDeleteRepairAlert}>
                <AlertDialogContent className="rounded-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle size={20} />
                            Delete Repair Record
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this repair record? This action cannot be undone and will be removed from the asset history.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl font-bold text-gray-700 bg-white border border-gray-200">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
                            onClick={confirmDeleteRepair}
                        >
                            Confirm Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>



            <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-4xl p-0 overflow-hidden bg-transparent border-none shadow-none flex items-center justify-center">
                    <img
                        src={asset?.image || ''}
                        alt={asset?.name}
                        className="w-full h-auto max-h-[85vh] object-contain rounded-2xl ring-1 ring-white/10 shadow-2xl"
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={completeMaintenanceModalOpen} onOpenChange={setCompleteMaintenanceModalOpen}>
                <DialogContent className="max-w-[600px] rounded-[32px] p-8 border-gray-100">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                            {maintenanceModalMode === 'edit' ? <Settings className="text-blue-500" /> : <CheckCircle2 className="text-green-500" />}
                            {maintenanceModalMode === 'edit' ? 'Edit Maintenance Details' : 'Complete Maintenance'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Maintenance Type</label>
                            <select
                                className="flex h-12 w-full rounded-2xl border-gray-100 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={maintenanceCompletionData.type}
                                onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, type: e.target.value })}
                            >
                                <option value="">Select type...</option>
                                {[
                                    'Oil Change', 'Major Service', 'Brake Check', 'Routine Inspection',
                                    'Hardware Cleaning', 'Software Update', 'Battery Replacement',
                                    'Tyre Rotation', 'Fluid Check', 'Emergency Repair',
                                    'Preventive Maintenance', 'Calibration', 'Safety Inspection'
                                ].map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Scheduled Date & Time</label>
                            <div className="grid grid-cols-2 gap-2">
                                <Input
                                    type="date"
                                    max="9999-12-31"
                                    className="h-12 w-full rounded-2xl border-gray-100 bg-gray-50 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={maintenanceCompletionData.scheduledDate}
                                    onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, scheduledDate: e.target.value })}
                                />
                                <Input
                                    type="time"
                                    className="h-12 w-full rounded-2xl border-gray-100 bg-gray-50 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={maintenanceCompletionData.scheduledTime}
                                    onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, scheduledTime: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(maintenanceModalMode === 'complete' || selectedMaintenanceRecord?.status === 'Completed') && (
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Completed Date</label>
                                    <Input
                                        type="date"
                                        max="9999-12-31"
                                        className="h-12 w-full rounded-2xl border-gray-100 bg-gray-50 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={maintenanceCompletionData.completedDate}
                                        onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, completedDate: e.target.value })}
                                    />
                                </div>
                            )}
                            <div className={cn("space-y-2", (maintenanceModalMode !== 'complete' && selectedMaintenanceRecord?.status !== 'Completed') && "col-span-2")}>
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Cost</label>
                                <div className="relative">
                                    <DollarSign size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input
                                        type="number"
                                        placeholder="0.00"
                                        className="h-12 w-full rounded-2xl border-gray-100 bg-gray-50 pl-9 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={maintenanceCompletionData.cost}
                                        onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, cost: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Vendor</label>
                                <Input
                                    placeholder="Service Provider"
                                    className="h-12 w-full rounded-2xl border-gray-100 bg-gray-50 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={maintenanceCompletionData.vendor}
                                    onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, vendor: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Performed By</label>
                                <Input
                                    placeholder="Technician/Staff"
                                    className="h-12 w-full rounded-2xl border-gray-100 bg-gray-50 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={maintenanceCompletionData.performedBy}
                                    onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, performedBy: e.target.value })}
                                />
                            </div>
                        </div>

                        {(maintenanceModalMode === 'complete' || selectedMaintenanceRecord?.status === 'Completed') && (
                            <div className="flex items-start gap-3 p-4 bg-blue-50/50 rounded-2xl border border-blue-100 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <Activity className="text-blue-500 shrink-0 mt-0.5" size={18} />
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-bold text-gray-900 leading-none">Setup reminder for next Scheduled Maintenance</label>
                                        <Switch
                                            checked={maintenanceCompletionData.setupReminder}
                                            onCheckedChange={(checked) => setMaintenanceCompletionData({ ...maintenanceCompletionData, setupReminder: checked })}
                                        />
                                    </div>
                                    <p className="text-[10px] font-medium text-gray-500 mt-2">
                                        Turn this on to set up reminder notifications once the record is saved. You'll be taken to the Reminders page automatically.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-900 uppercase tracking-widest">Description</label>
                            <textarea
                                className="flex min-h-[100px] w-full rounded-2xl border-gray-100 bg-gray-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Service details..."
                                value={maintenanceCompletionData.description}
                                onChange={(e) => setMaintenanceCompletionData({ ...maintenanceCompletionData, description: e.target.value })}
                            ></textarea>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
                        <Button
                            variant="ghost"
                            className="rounded-2xl h-12 font-bold px-8 w-full sm:w-auto"
                            onClick={() => setCompleteMaintenanceModalOpen(false)}
                            disabled={completingMaintenance}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-2xl h-12 font-bold px-8 w-full sm:w-auto"
                            onClick={handleCompleteMaintenance}
                            disabled={completingMaintenance || (maintenanceModalMode === 'complete' && !maintenanceCompletionData.completedDate)}
                        >
                            {completingMaintenance ? 'Saving...' : (maintenanceModalMode === 'edit' ? 'Update Details' : 'Mark Complete')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 size={20} />
                            Delete Asset
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-600">
                            <span className="font-bold text-gray-900 block mb-2">{asset?.name} ({asset?.tag})</span>
                            This asset will be moved to the recycle bin for 180 days. After 180 days, the asset will be <span className="font-bold text-red-600">permanently deleted</span> and cannot be recovered. You can reinstate it from the Recycle Bin tab during this period.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="space-y-3 py-2">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Current Status:</span>
                            <Badge className={cn("rounded-lg border-0 font-bold px-2.5 py-0.5 text-[10px]", getStatusStyles(asset?.status || 'Available' as AssetStatus))}>
                                {asset?.status}
                            </Badge>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block">
                                Approved By <span className="text-red-500">*</span>
                            </label>
                            <Select value={selectedApprover} onValueChange={setSelectedApprover}>
                                <SelectTrigger className="rounded-xl h-11">
                                    <SelectValue placeholder="Select who approved the deletion..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {approvers.map((approver) => (
                                        <SelectItem key={approver.id} value={approver.id.toString()}>
                                            {approver.name} — {approver.userRole}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {!selectedApprover && (
                                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    Please select a Manager, Director, Senior Manager, or Admin who approved this deletion
                                </p>
                            )}
                        </div>
                    </div>

                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl" onClick={() => { setShowDeleteConfirm(false); setSelectedApprover(''); }}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
                            disabled={!selectedApprover || deleting}
                            onClick={(e) => {
                                e.preventDefault();
                                confirmDelete();
                            }}
                        >
                            {deleting ? 'Deleting...' : 'Delete Asset'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Warning Dialog */}
            <AlertDialog open={showDeleteWarning} onOpenChange={setShowDeleteWarning}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                            <AlertTriangle size={20} />
                            Cannot Delete Asset
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-600">
                            This asset's current status is <Badge className={cn("rounded-lg border-0 font-bold px-2.5 py-0.5 text-[10px] mx-1", getStatusStyles(asset?.status || 'Available' as AssetStatus))}>{asset?.status}</Badge>.
                            <span className="block mt-2">An asset can only be deleted when its status is <span className="font-bold">Damaged</span>, <span className="font-bold">Needs Repair</span>, <span className="font-bold">Disposed</span>, or <span className="font-bold">Lost</span>.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction className="rounded-xl" onClick={() => setShowDeleteWarning(false)}>OK</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reminders Dialog */}
            {/* AddReminder was removed - navigating to AddReminderPage instead */}

            <InspectionQRModal
                isOpen={isQRModalOpen}
                onClose={() => setIsQRModalOpen(false)}
                asset={asset}
                hasInspectionTemplate={hasInspectionTemplate}
            />
        </div>
    );
};

export default AssetDetails;
